/**
 * Filter Service - Business logic for product filtering
 */

import type { FilterParams } from '../../types/index.js';

const UNIT_LABELS: Record<string, string> = {
    weight: 'kg',
    volume: 'L',
    piece: 'pcs'
};

/**
 * Get translation function
 */
function t(key: string): string {
    if (window.GROCERY_TRANSLATIONS && window.GROCERY_TRANSLATIONS[key]) {
        return window.GROCERY_TRANSLATIONS[key];
    }
    return key;
}

/**
 * Filter Service - Business logic for product filtering
 */
export class FilterService {
    private filters: FilterParams;

    constructor(initialFilters: FilterParams = {}) {
        this.filters = { ...initialFilters };
    }

    /**
     * Set a single filter value
     */
    setFilter(key: string, value: any): void {
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        const isEmptyValue = value === '' || value === null || value === undefined;

        // Remove filter entirely when cleared so deselects propagate
        if (isEmptyArray || isEmptyValue) {
            delete this.filters[key];
            return;
        }

        this.filters[key] = value;
    }

    /**
     * Set multiple filter values
     */
    setFilters(newFilters: FilterParams): void {
        this.filters = { ...this.filters, ...newFilters };
    }

    /**
     * Clear all filters
     */
    clearFilters(): void {
        this.filters = {};
        this.resetFilters();
    }

    /**
     * Get current filter values from DOM
     */
    getFilters(): FilterParams {
        const filters: FilterParams = { ...this.filters };
        
        // Search query
        const searchInput = document.querySelector<HTMLInputElement>('#filter-search-top, #filter-search, #filter-search-mobile');
        if (searchInput && searchInput.value.trim()) {
            filters.search = searchInput.value.trim();
        }
        
        // Sort by
        const sortBySelect = document.querySelector<HTMLSelectElement>('#filter-sort-by, #filter-sort-by-mobile');
        if (sortBySelect && sortBySelect.value) {
            filters.sort_by = sortBySelect.value;
        }
        
        // Sort order (from stored filter)
        if (this.filters.order) {
            filters.order = this.filters.order;
        }
        
        // On sale
        const onSaleCheckbox = document.querySelector<HTMLInputElement>('#filter-on-sale, #filter-on-sale-mobile');
        if (onSaleCheckbox && onSaleCheckbox.checked) {
            filters.on_sale = 'true';
        }
        
        // Price range
        const minPriceInput = document.querySelector<HTMLInputElement>('#filter-min-price, #filter-min-price-mobile');
        const maxPriceInput = document.querySelector<HTMLInputElement>('#filter-max-price, #filter-max-price-mobile');
        if (minPriceInput && minPriceInput.value) {
            filters.min_price = minPriceInput.value;
        }
        if (maxPriceInput && maxPriceInput.value) {
            filters.max_price = maxPriceInput.value;
        }
        
        // Unit range
        const unitGroupInput = document.querySelector<HTMLInputElement>('#filter-unit-group, #filter-unit-group-mobile');
        if (unitGroupInput && unitGroupInput.value) {
            const [group, minMax] = unitGroupInput.value.split(':');
            if (group && minMax) {
                const [min, max] = minMax.split('-');
                if (min) filters.unit_min = min;
                if (max) filters.unit_max = max;
                filters.unit_group = group;
            }
        }
        
        // Stores
        const storeCheckboxes = document.querySelectorAll<HTMLInputElement>('#filter-stores-list input[type="checkbox"]:checked, #filter-stores-list-mobile input[type="checkbox"]:checked');
        if (storeCheckboxes.length > 0) {
            const ids = Array.from(storeCheckboxes).map(cb => cb.value);
            filters.store_id = ids;
            filters.store_ids = ids;
        }
        
        return filters;
    }

    /**
     * Update unit section UI
     */
    updateUnitSectionUI(section: Element | null, value: string): void {
        if (!section) return;
        
        const hiddenInput = section.querySelector<HTMLInputElement>('[data-unit-input]');
        const normalized = value || '';
        
        if (hiddenInput) {
            hiddenInput.value = normalized;
        }
        
        const activeGroup = normalized.split(':')[0] || '';
        
        section.querySelectorAll('[data-unit-group-trigger]').forEach(btn => {
            const isActive = btn.getAttribute('data-group') === activeGroup;
            btn.classList.toggle('unit-toggle-active', isActive);
        });
    }

    /**
     * Sync unit section with current value
     */
    syncUnitSection(section: Element | null): void {
        if (!section) return;
        
        const hiddenInput = section.querySelector<HTMLInputElement>('[data-unit-input]');
        let value = hiddenInput?.value;
        
        if (!value) {
            const defaultButton = section.querySelector<HTMLElement>('[data-unit-group-trigger][data-default-unit-value]');
            value = defaultButton?.getAttribute('data-default-unit-value') || 
                    section.querySelector<HTMLElement>('[data-unit-unit-trigger]')?.getAttribute('data-unit-value') || '';
        }
        
        this.updateUnitSectionUI(section, value);
    }

    /**
     * Sync all unit sections
     */
    syncAllUnitSections(): void {
        document.querySelectorAll('[data-unit-section]').forEach(section => this.syncUnitSection(section));
    }

    /**
     * Sync unit section with specific value
     */
    syncUnitSectionWithValue(section: Element | null, value: string): void {
        if (!section) return;
        
        let nextValue = value;
        if (!nextValue) {
            nextValue = section.querySelector<HTMLElement>('[data-unit-unit-trigger]')?.getAttribute('data-unit-value') || '';
        }
        
        this.updateUnitSectionUI(section, nextValue);
    }

    /**
     * Format unit range value for display
     */
    formatUnitRangeValue(value: number | string | null | undefined, group: string): string {
        if (value === null || value === undefined) {
            return '0';
        }
        
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return '0';
        }
        
        let formatted: string;
        if (num >= 10) {
            formatted = num.toFixed(0);
        } else if (num >= 1) {
            formatted = num.toFixed(2).replace(/\.?0+$/, '');
        } else {
            formatted = num.toFixed(3).replace(/\.?0+$/, '');
        }
        
        const label = group === 'piece' ? t('pcs') : (UNIT_LABELS[group] || '');
        return label ? `${formatted} ${label}` : formatted;
    }

    /**
     * Reset all filters to default state
     */
    resetFilters(): void {
        // Reset search
        document.querySelectorAll<HTMLInputElement>('#filter-search-top, #filter-search, #filter-search-mobile').forEach(input => {
            input.value = '';
        });
        
        // Reset sort
        document.querySelectorAll<HTMLSelectElement>('#filter-sort-by, #filter-sort-by-mobile').forEach(select => {
            select.value = 'discount_percentage';
        });
        
        // Reset on sale
        document.querySelectorAll<HTMLInputElement>('#filter-on-sale, #filter-on-sale-mobile').forEach(checkbox => {
            checkbox.checked = false;
        });
        
        // Reset price range
        document.querySelectorAll<HTMLInputElement>('#filter-min-price, #filter-max-price, #filter-min-price-mobile, #filter-max-price-mobile').forEach(input => {
            input.value = '';
        });
        
        // Reset unit range
        document.querySelectorAll<HTMLInputElement>('#filter-unit-group, #filter-unit-group-mobile').forEach(input => {
            input.value = '';
        });
        this.syncAllUnitSections();
        
        // Reset store checkboxes
        document.querySelectorAll<HTMLInputElement>('#filter-stores-list input[type="checkbox"]:checked, #filter-stores-list-mobile input[type="checkbox"]:checked').forEach(cb => {
            cb.checked = false;
        });
        
        // Remove selected class from labels
        document.querySelectorAll('.store-filter-item.is-selected').forEach(el => {
            el.classList.remove('is-selected');
        });
    }
}
